/**
 * Cognito hosted UI — OAuth 2.0 authorization code + PKCE (public client, no
 * secret) against the ADMIN pool (rhino-{env}-admin-users).
 *
 *  - "Sign in with Google" → /oauth2/authorize?identity_provider=GoogleWorkspace
 *  - "Admin login" (break-glass native users) → /oauth2/authorize without an IdP
 *  - /auth/callback exchanges the code at /oauth2/token (verifier + state kept
 *    in sessionStorage for the round-trip only)
 *  - refresh via /oauth2/token grant_type=refresh_token
 *  - logout via /logout
 */
import { env } from '../config/env';
import { codeChallengeS256, generateCodeVerifier, generateState } from './pkce';

const PENDING_KEY = 'rhino.ops.oauth.pending';
const SCOPES = 'openid email profile';

export type IdentityProvider = 'GoogleWorkspace' | 'Google';

export interface TokenSet {
  idToken: string;
  accessToken: string;
  /** Absent on refresh responses (Cognito does not rotate refresh tokens). */
  refreshToken?: string;
  expiresIn: number;
}

interface PendingLogin {
  state: string;
  verifier: string;
  returnTo: string;
}

export type OAuthErrorCode =
  | 'idp_error'
  | 'missing_code'
  | 'state_mismatch'
  | 'token_exchange_failed'
  | 'invalid_grant'
  | 'network';

export class OAuthError extends Error {
  readonly code: OAuthErrorCode;
  constructor(code: OAuthErrorCode, message: string) {
    super(message);
    this.name = 'OAuthError';
    this.code = code;
  }
}

/** Only same-app relative paths are allowed as post-login destinations. */
export function safeReturnTo(value: string | null | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/auth/')) {
    return '/work';
  }
  if (value === '/login') return '/work';
  return value;
}

export interface AuthorizeOptions {
  identityProvider?: IdentityProvider;
  returnTo?: string;
}

/** Builds the hosted-UI authorize URL and stores the PKCE verifier + state. */
export async function buildAuthorizeUrl(options: AuthorizeOptions = {}): Promise<string> {
  const verifier = generateCodeVerifier();
  const state = generateState();
  const pending: PendingLogin = { state, verifier, returnTo: safeReturnTo(options.returnTo) };
  window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: env.cognitoClientId,
    redirect_uri: env.oauthRedirectUri,
    scope: SCOPES,
    state,
    code_challenge: await codeChallengeS256(verifier),
    code_challenge_method: 'S256',
  });
  if (options.identityProvider) params.set('identity_provider', options.identityProvider);
  return `${env.cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

export async function beginLogin(options: AuthorizeOptions = {}): Promise<void> {
  window.location.assign(await buildAuthorizeUrl(options));
}

function readPending(): PendingLogin | null {
  const raw = window.sessionStorage.getItem(PENDING_KEY);
  // One-time use: a code/verifier pair must never be replayed.
  window.sessionStorage.removeItem(PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingLogin>;
    if (typeof parsed.state === 'string' && typeof parsed.verifier === 'string') {
      return { state: parsed.state, verifier: parsed.verifier, returnTo: safeReturnTo(parsed.returnTo) };
    }
  } catch {
    /* fall through */
  }
  return null;
}

interface TokenEndpointResponse {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
}

async function postToken(body: URLSearchParams): Promise<TokenSet> {
  let response: Response;
  try {
    response = await fetch(`${env.cognitoDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch {
    throw new OAuthError('network', 'We couldn’t reach the sign-in service. Check your connection and try again.');
  }

  let payload: TokenEndpointResponse = {};
  try {
    payload = (await response.json()) as TokenEndpointResponse;
  } catch {
    payload = {};
  }

  if (!response.ok || !payload.id_token || !payload.access_token) {
    if (payload.error === 'invalid_grant') {
      throw new OAuthError('invalid_grant', 'Your sign-in link has expired. Please sign in again.');
    }
    throw new OAuthError('token_exchange_failed', 'Sign-in could not be completed. Please try again.');
  }

  return {
    idToken: payload.id_token,
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresIn: payload.expires_in ?? 3600,
  };
}

/**
 * Completes the authorization-code flow from the callback URL's query string.
 * Verifies `state` against the value stored before redirecting.
 */
export async function completeLogin(search: string): Promise<{ tokens: TokenSet; returnTo: string }> {
  const params = new URLSearchParams(search);
  const pending = readPending();

  const idpError = params.get('error');
  if (idpError) {
    const description = params.get('error_description');
    throw new OAuthError(
      'idp_error',
      description
        ? `Sign-in was rejected: ${description.replace(/\+/g, ' ')}`
        : 'Sign-in was cancelled or rejected.',
    );
  }

  const code = params.get('code');
  const state = params.get('state');
  if (!code || !state) {
    throw new OAuthError('missing_code', 'The sign-in response was incomplete. Please sign in again.');
  }
  if (!pending || pending.state !== state) {
    throw new OAuthError('state_mismatch', 'This sign-in attempt could not be verified. Please sign in again.');
  }

  const tokens = await postToken(
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: env.cognitoClientId,
      code,
      redirect_uri: env.oauthRedirectUri,
      code_verifier: pending.verifier,
    }),
  );
  return { tokens, returnTo: pending.returnTo };
}

export function refreshTokens(refreshToken: string): Promise<TokenSet> {
  return postToken(
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: env.cognitoClientId,
      refresh_token: refreshToken,
    }),
  );
}

/** Hosted-UI logout (ends the Cognito session cookie), returning to /login. */
export function buildLogoutUrl(): string {
  const logoutUri = `${new URL(env.oauthRedirectUri).origin}/login`;
  const params = new URLSearchParams({ client_id: env.cognitoClientId, logout_uri: logoutUri });
  return `${env.cognitoDomain}/logout?${params.toString()}`;
}
